import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token, typography } from '@figma/fpl-tokens/tokens.stylex';
import { TextLgRg } from '@/UI/AppTypography/AppTypography';
import { CaretIcon } from '@/UI/Icons';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer } from '@/UI/styles';
import type { FolderResponseDto } from '@/types/folder.types';

interface BreadcrumbsProps {
  folderPath: FolderResponseDto[];
  onNavigate: (folderId?: string | null) => void;
  rootName?: string;
}

export const Breadcrumbs = ({ folderPath, onNavigate, rootName }: BreadcrumbsProps) => {
  const { t } = useTranslation();

  const handleClick = useCallback(
    (folderId?: string | null) => {
      onNavigate(folderId);
    },
    [onNavigate],
  );

  return (
    <FlexCenVer role="navigation" aria-label={t(I18N_KEYS.MAIN_DASHBOARD.BREADCRUMB_ARIA)}>
      {/* My Files (root) */}
      <TextLgRg
        onClick={() => handleClick(null)}
        sx={{
          color: folderPath.length === 0 ? token.text : token.textSecondary,
          cursor: folderPath.length === 0 ? 'default' : 'pointer',
          borderRadius: 0.5,
          transition: 'all 0.2s ease-in-out',
          // The page name lines up with the workspace name above it. The old inset pushed "Tools"
          // to the right of that name.
          pl: 0,
          pr: 0.75,
          fontSize: typography.textBodyLargeFontSize,
          '&:hover': folderPath.length > 0 ? { color: token.text, bgcolor: color.White08_T } : {},
        }}
      >
        {rootName || t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES)}
      </TextLgRg>

      {folderPath.map((folder, index) => (
        <FlexCenVer key={folder.id}>
          <CaretIcon width={12} height={12} style={{ color: token.textSecondary, transform: 'rotate(-90deg)' }} />
          <TextLgRg
            onClick={() => handleClick(folder.id)}
            sx={{
              color: index === folderPath.length - 1 ? token.text : token.textSecondary,
              cursor: index === folderPath.length - 1 ? 'default' : 'pointer',
              borderRadius: 0.5,
              px: 0.75,
              transition: 'all 0.2s ease-in-out',
              fontSize: typography.textBodyLargeFontSize,
              '&:hover': index !== folderPath.length - 1 ? { color: token.text, bgcolor: color.White08_T } : {},
            }}
          >
            {folder.name}
          </TextLgRg>
        </FlexCenVer>
      ))}
    </FlexCenVer>
  );
};
